import { inject, Injectable } from '@angular/core';
import {
  AppConfigSettings,
  CoreService,
  getActiveTenant$,
  userChanged$,
} from '@arc/arc-shared-frontend';
import { Store } from '@ngrx/store';
import { Amplify } from 'aws-amplify';
import { events, EventsChannel } from 'aws-amplify/data';
import {
  combineLatest,
  finalize,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  EmailVerificationPushNotification,
  PaymentAccountsPushNotification,
  SepaMandateEnrichmentPushNotification,
} from '../models';

/* istanbul ignore next */
@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private destroy$ = new Subject<void>();
  private currentChannel: EventsChannel | null = null;
  private channelSubscription: Subscription | null = null;
  private emailVerificationSubscription: Subscription | null = null;
  private store = inject(Store<any>);
  private appConfig = inject(AppConfigSettings);
  private coreService = inject(CoreService);
  private environment = this.appConfig.getconfig('environment');
  private region = this.appConfig.getconfig('region');

  constructor() {}

  /**
   * Subscribe to user "paymentAccounts" events with automatic cleanup.
   * Connection automatically disposes after receiving a message or on error.
   *
   * @param success Callback invoked on receiving a notification.
   * @param error Callback invoked on encountering an error.
   * @returns Subscription that can be used to unsubscribe if needed.
   */
  subscribeToUserPaymentAccountsEvents(
    success: (notification: PaymentAccountsPushNotification) => void,
    error: (err: any) => void,
  ): Subscription {
    events.closeAll();
    return combineLatest([
      userChanged$(this.store),
      getActiveTenant$(this.store),
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([user, tenant]) => {
          return this.establishUserConnection<PaymentAccountsPushNotification>(
            user.userId,
            tenant.tenantId,
            user.accessToken,
            'paymentAccounts',
            success,
            error,
          );
        }),
        finalize(async () => {
          await this.performCleanup();
        }),
      )
      .subscribe();
  }

  /**
   * Subscribe to user "sepaMandateEnrichment" events with automatic cleanup.
   * Connection automatically disposes after receiving a message or on error.
   *
   * Establishes a channel subscription at:
   *   /users/{userId}/tenants/{tenantId}/sepaMandateEnrichment
   *
   * Mirrors subscribeToUserPaymentAccountsEvents (logged-in wallet buyer →
   * user.accessToken). Consumer wiring (gating, idle-timeout, re-subscribe)
   * lives in wallet-core (BTPAY-7127), not here.
   *
   * @param success Callback invoked on receiving an enrichment notification.
   * @param error Callback invoked on encountering an error.
   * @returns Subscription that can be used to unsubscribe if needed.
   */
  subscribeToUserSepaMandateEnrichmentEvents(
    success: (notification: SepaMandateEnrichmentPushNotification) => void,
    error: (err: any) => void,
  ): Subscription {
    events.closeAll();
    return combineLatest([
      userChanged$(this.store),
      getActiveTenant$(this.store),
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([user, tenant]) => {
          return this.establishUserConnection<SepaMandateEnrichmentPushNotification>(
            user.userId,
            tenant.tenantId,
            user.accessToken,
            'sepaMandateEnrichment',
            success,
            error,
          );
        }),
        finalize(async () => {
          await this.performCleanup();
        }),
      )
      .subscribe();
  }

  /**
   * Unsubscribe from email verification events, if an active subscription exists.
   */
  unsubscribeFromEmailVerificationEvents(): void {
    this.emailVerificationSubscription?.unsubscribe();
    this.emailVerificationSubscription = null;
  }

  /**
   * Subscribe to email verification events for an anonymous platform user.
   * Connection automatically disposes after receiving a message or on error.
   *
   * Establishes a channel subscription at:
   *   /users/{userId}/tenants/{tenantId}/paymentlinkemailverification
   *
   * @param success Callback invoked on receiving a verification notification.
   * @param error Callback invoked on encountering an error.
   * @returns Subscription that can be used to unsubscribe if needed.
   */
  subscribeToEmailVerificationEvents(
    success: (notification: EmailVerificationPushNotification) => void,
    error: (err: any) => void,
  ): Subscription {
    this.emailVerificationSubscription?.unsubscribe();
    events.closeAll();
    this.emailVerificationSubscription = combineLatest([
      userChanged$(this.store),
      getActiveTenant$(this.store),
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([user, tenant]) => {
          return this.establishUserConnection<EmailVerificationPushNotification>(
            user.userId,
            tenant.tenantId,
            this.coreService.getAuthTokenFromLocalStorage(),
            `paymentlinkemailverification`,
            success,
            error,
          );
        }),
        finalize(async () => {
          await this.performCleanup();
        }),
      )
      .subscribe();
    return this.emailVerificationSubscription;
  }

  // Actual connection establishment — supports both user.userId and platformUserId
  private async establishUserConnection<T>(
    userId: string,
    tenantId: string,
    accessToken: string,
    channel: string,
    success: (data: T) => void,
    handleError: (err: any) => void,
  ): Promise<void> {
    const channelPath = `/users/${userId}/tenants/${tenantId}/${channel}`;

    // Always cleanup previous connection first
    await this.performCleanup();

    if (!accessToken) {
      const err = new Error(
        `[PushNotificationsService] Missing access token for channel: ${channelPath}`,
      );
      handleError(err);
      return;
    }

    try {
      // Configure Amplify
      this.configureAmplify(accessToken);

      // Connect to the specific channel
      this.currentChannel = await events.connect(channelPath);

      // Subscribe to messages
      this.channelSubscription = this.currentChannel.subscribe({
        next: (data: T) => {
          success(data);
          this.forceDestroy();
        },
        error: (error: any) => {
          handleError(error);
          this.forceDestroy();
        },
      });
    } catch (error) {
      // Handle connection failures and cleanup gracefully
      handleError(error);
      this.forceDestroy();
      throw error;
    }
  }

  private buildDomain(): string {
    return this.environment !== 'prod'
      ? `app-${this.environment}.billtrust.com`
      : 'app.billtrust.com';
  }

  private configureAmplify(authToken: string) {
    Amplify.configure(
      {
        API: {
          Events: {
            endpoint: `https://notify.${this.buildDomain()}/event`,
            region: this.region || 'us-east-1',
            defaultAuthMode: 'oidc',
          },
        },
      },
      {
        Auth: {
          tokenProvider: {
            async getTokens() {
              return {
                accessToken: {
                  toString: () => authToken,
                  payload: {},
                },
              };
            },
          },
        },
      },
    );
  }

  // Auto cleanup method called by finalize operator
  private async performCleanup() {
    if (this.currentChannel) {
      this.currentChannel.close();
      this.channelSubscription?.unsubscribe();
      this.channelSubscription = null;
      this.currentChannel = null;
    }
    await events.closeAll();
  }

  //This will trigger the cleanup immediately
  private forceDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
  }
}
